import React, { useEffect, useState } from 'react';
import { Box, Checkbox, FormControlLabel, TextField, Typography } from '@mui/material';
import { RadioButtonUncheckedOutlined, RadioButtonCheckedOutlined } from '@mui/icons-material';

interface SnowflakeAccountFormProps {
  connectionDetails: string;
  refreshRequired: boolean;
  handleUpdateNewGameData: any;
  onValidityChange?: (isValid: boolean) => void;
  disabled?: boolean;
}

// const SNOWFLAKE_USERNAME_REGEX = /^[a-zA-Z0-9_]+$/;
// const SNOWFLAKE_PASSWORD_REGEX = /^[a-zA-Z0-9_]+$/;
// const SNOWFLAKE_HOST_REGEX =
//   /^[a-zA-Z0-9-]+\.east-us\.snowflakecomputing\.com$/;
// const SNOWFLAKE_ROLE_REGEX = /^[a-zA-Z0-9_]+$/;
// const SNOWFLAKE_WAREHOUSE_REGEX = /^[a-zA-Z0-9_]+$/;
// const SNOWFLAKE_DATABASE_NAME_REGEX = /^[a-zA-Z0-9_]+$/;
// const SNOWFLAKE_SCHEMA_REGEX = /^[a-zA-Z0-9_]+$/;

const SnowflakeAccountForm: React.FC<SnowflakeAccountFormProps> = ({
  connectionDetails,
  refreshRequired,
  handleUpdateNewGameData,
  onValidityChange,
  disabled = false,
}) => {
  const fieldValues = JSON.parse(connectionDetails || '{}');
  const [isTouched, setIsTouched] = useState({
    username: false,
    password: false,
    host: false,
    role: false,
    warehouse: false,
    databaseName: false,
    schema: false,
  });

  useEffect(() => {
    const isUsernameValid = !!fieldValues.username;
    const isPasswordValid = !!fieldValues.password;
    const isHostValid = !!fieldValues.host;
    const isRoleValid = !!fieldValues.role;
    const isWarehouseValid = !!fieldValues.warehouse;
    const isDatabaseNameValid = !!fieldValues.databaseName;
    const isSchemaValid = !!fieldValues.schema;

    const isAllValid =
      isUsernameValid &&
      isPasswordValid &&
      isHostValid &&
      isRoleValid &&
      isWarehouseValid &&
      isDatabaseNameValid &&
      isSchemaValid;
    onValidityChange?.(isAllValid);
  }, [
    fieldValues.username,
    fieldValues.password,
    fieldValues.host,
    fieldValues.role,
    fieldValues.warehouse,
    fieldValues.databaseName,
    fieldValues.schema,
  ]);

  return (
    <>
      <TextField
        label="Username"
        placeholder="e.g. pvx_read_only_user"
        sx={{
          width: '100%',
        }}
        value={fieldValues.username}
        error={!fieldValues.username && isTouched.username}
        onBlur={(e) => {
          setIsTouched((prev) => ({
            ...prev,
            username: true,
          }));
        }}
        onChange={(e) =>
          handleUpdateNewGameData(
            'connectionDetails',
            JSON.stringify({
              ...fieldValues,
              username: e.target.value,
            }),
          )
        }
        disabled={disabled}
      />
      <TextField
        label="Password"
        placeholder="Read-only user password"
        sx={{
          width: '100%',
        }}
        value={fieldValues.password}
        error={!fieldValues.password && isTouched.password}
        onBlur={(e) => {
          setIsTouched((prev) => ({
            ...prev,
            password: true,
          }));
        }}
        onChange={(e) =>
          handleUpdateNewGameData(
            'connectionDetails',
            JSON.stringify({
              ...fieldValues,
              password: e.target.value,
            }),
          )
        }
        disabled={disabled}
      />
      <TextField
        label="Host"
        placeholder="e.g. xy12345.east-us.snowflakecomputing.com"
        sx={{
          width: '100%',
        }}
        value={fieldValues.host}
        error={!fieldValues.host && isTouched.host}
        onBlur={(e) => {
          setIsTouched((prev) => ({
            ...prev,
            host: true,
          }));
        }}
        onChange={(e) =>
          handleUpdateNewGameData(
            'connectionDetails',
            JSON.stringify({
              ...fieldValues,
              host: e.target.value,
            }),
          )
        }
        disabled={disabled}
      />
      <TextField
        label="Role"
        placeholder="e.g. READ_ONLY_ROLE"
        sx={{
          width: '100%',
        }}
        value={fieldValues.role}
        error={!fieldValues.role && isTouched.role}
        onBlur={(e) => {
          setIsTouched((prev) => ({
            ...prev,
            role: true,
          }));
        }}
        onChange={(e) =>
          handleUpdateNewGameData(
            'connectionDetails',
            JSON.stringify({
              ...fieldValues,
              role: e.target.value,
            }),
          )
        }
        disabled={disabled}
      />
      <TextField
        label="Warehouse"
        placeholder="e.g. COMPUTE_WH"
        sx={{
          width: '100%',
        }}
        value={fieldValues.warehouse}
        error={!fieldValues.warehouse && isTouched.warehouse}
        onBlur={(e) => {
          setIsTouched((prev) => ({
            ...prev,
            warehouse: true,
          }));
        }}
        onChange={(e) =>
          handleUpdateNewGameData(
            'connectionDetails',
            JSON.stringify({
              ...fieldValues,
              warehouse: e.target.value,
            }),
          )
        }
        disabled={disabled}
      />
      <TextField
        label="Database Name"
        placeholder="e.g. PVX_DB"
        sx={{
          width: '100%',
        }}
        value={fieldValues.databaseName}
        error={!fieldValues.databaseName && isTouched.databaseName}
        onBlur={(e) => {
          setIsTouched((prev) => ({
            ...prev,
            databaseName: true,
          }));
        }}
        onChange={(e) =>
          handleUpdateNewGameData(
            'connectionDetails',
            JSON.stringify({
              ...fieldValues,
              databaseName: e.target.value,
            }),
          )
        }
        disabled={disabled}
      />
      <TextField
        label="Schema"
        placeholder="e.g. PUBLIC"
        sx={{
          width: '100%',
        }}
        value={fieldValues.schema}
        error={!fieldValues.schema && isTouched.schema}
        onBlur={(e) => {
          setIsTouched((prev) => ({
            ...prev,
            schema: true,
          }));
        }}
        onChange={(e) =>
          handleUpdateNewGameData(
            'connectionDetails',
            JSON.stringify({
              ...fieldValues,
              schema: e.target.value,
            }),
          )
        }
        disabled={disabled}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography fontSize="0.85rem" fontWeight={700} sx={{ color: '#162936' }}>
          Monthly Refresh
        </Typography>
        <Box sx={{ display: 'flex', gap: '1rem' }}>
          {['Yes', 'No'].map((label, i) => {
            const value = i === 0;
            return (
              <FormControlLabel
                key={label}
                sx={{
                  width: 'fit-content',
                  borderRadius: '0.5rem',
                  mx: 0,
                  '& .MuiFormControlLabel-label': {
                    fontFamily: 'Manrope, sans-serif',
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    color: '#162936',
                  },
                }}
                label={label}
                control={
                  <Checkbox
                    checked={refreshRequired === value}
                    onChange={() => handleUpdateNewGameData('refreshRequired', value)}
                    icon={<RadioButtonUncheckedOutlined />}
                    checkedIcon={<RadioButtonCheckedOutlined />}
                    disabled={disabled}
                  />
                }
              />
            );
          })}
        </Box>
      </Box>
    </>
  );
};

export default SnowflakeAccountForm;
